import { readDocumentCookies, writeDocumentCookie } from "@/lib/browserCookies";

const STORAGE_KEY = "analytics.marketingSessionId";
const COOKIE_NAME = "ee_marketing_session_id";
const MAX_ID_LENGTH = 255;
const VALID_ID_PATTERN = /^[A-Za-z0-9_-]{16,255}$/;

let memoizedMarketingSessionId: string | undefined;
let pendingObservedIdentityBoundary = false;
let marketingIdentityGeneration = 0;
const identityBoundaryListeners = new Set<() => void>();

const notifyIdentityBoundary = (): boolean => {
  marketingIdentityGeneration += 1;
  let handled = false;
  for (const listener of identityBoundaryListeners) {
    try {
      listener();
      handled = true;
    } catch {
      // A boundary must still be adopted even if one cleanup listener fails.
    }
  }
  return handled;
};

const isValidMarketingSessionId = (value: string | undefined): value is string =>
  Boolean(value && value.length <= MAX_ID_LENGTH && VALID_ID_PATTERN.test(value));

const readSessionStorage = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY) ?? undefined;
    return isValidMarketingSessionId(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

const writeSessionStorage = (value: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // A first-party session cookie still carries the id through OAuth when storage is blocked.
  }
};

const readCookie = (): string | undefined => {
  const cookie = readDocumentCookies();
  for (const part of cookie ? cookie.split(/;\s*/) : []) {
    const separatorIndex = part.indexOf("=");
    const name = separatorIndex >= 0 ? part.slice(0, separatorIndex) : part;
    if (name !== COOKIE_NAME) continue;
    const encodedValue = separatorIndex >= 0 ? part.slice(separatorIndex + 1) : "";
    try {
      const value = decodeURIComponent(encodedValue);
      return isValidMarketingSessionId(value) ? value : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const writeCookie = (value: string): void => {
  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
  // A session cookie intentionally has no Max-Age. SameSite=Lax carries it through the
  // top-level OAuth callback while keeping it off cross-site subresource requests.
  writeDocumentCookie(`${COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${isHttps ? "; Secure" : ""}`);
};

const generateMarketingSessionId = (): string => {
  if (typeof crypto !== "undefined") {
    try {
      if (typeof crypto.randomUUID === "function") return `ms_${crypto.randomUUID()}`;
      if (typeof crypto.getRandomValues === "function") {
        const entropy = crypto.getRandomValues(new Uint32Array(4));
        return `ms_${[...entropy].map((value) => value.toString(16).padStart(8, "0")).join("")}`;
      }
    } catch {
      // Fall through to a non-cryptographic correlation fallback for restricted browsers.
    }
  }

  const timestamp = Date.now().toString(36);
  const entropy = Math.random().toString(36).slice(2, 18);
  return `ms_${timestamp}_${entropy}`;
};

const persist = (value: string): string => {
  memoizedMarketingSessionId = value;
  writeSessionStorage(value);
  writeCookie(value);
  return value;
};

/**
 * First-party browsing identity for one marketing journey. It is independent from the
 * authenticated application session.
 */
export const getMarketingSessionId = (): string => {
  // The cookie is shared by every tab, so it is the authority on every read. When one tab
  // rotates at logout/account-switch, another already-open tab must adopt that boundary before
  // its next request instead of continuing with its tab-local memo/sessionStorage value.
  const cookieValue = readCookie();
  if (cookieValue) {
    const storedValue = readSessionStorage();
    const previousValue = storedValue ?? memoizedMarketingSessionId;
    const crossedIdentityBoundary = Boolean(previousValue && previousValue !== cookieValue);
    if (crossedIdentityBoundary) {
      pendingObservedIdentityBoundary = true;
    }
    memoizedMarketingSessionId = cookieValue;
    if (storedValue !== cookieValue) writeSessionStorage(cookieValue);
    if (crossedIdentityBoundary && notifyIdentityBoundary()) {
      pendingObservedIdentityBoundary = false;
    }
    return cookieValue;
  }

  const existing = readSessionStorage() ?? memoizedMarketingSessionId;
  return persist(existing ?? generateMarketingSessionId());
};

/**
 * Side-effect-free snapshot for React render bookkeeping. Unlike getMarketingSessionId, this
 * never adopts the shared cookie, persists an id, or notifies identity-boundary listeners.
 */
export const peekMarketingSessionId = (): string | undefined => memoizedMarketingSessionId ?? readSessionStorage();

/** Rotates the marketing journey at an explicit identity boundary (logout/user change). */
export const rotateMarketingSessionId = (): string => {
  const replacement = persist(generateMarketingSessionId());
  notifyIdentityBoundary();
  return replacement;
};

/** Observes a shared-cookie journey replacement detected after this document booted. */
export const subscribeMarketingIdentityBoundaries = (listener: () => void): (() => void) => {
  identityBoundaryListeners.add(listener);
  return () => identityBoundaryListeners.delete(listener);
};

/** Monotonic external-store snapshot used to retrack the current page after a journey boundary. */
export const getMarketingIdentityGeneration = (): number => marketingIdentityGeneration;

type ObservedMarketingIdentityBoundary = {
  journeyReplaced: boolean;
};

export const consumeObservedMarketingIdentityBoundary = (): ObservedMarketingIdentityBoundary => {
  const journeyReplaced = pendingObservedIdentityBoundary;
  pendingObservedIdentityBoundary = false;
  return { journeyReplaced };
};

export const MARKETING_SESSION_HEADER_NAME = "x-marketing-session-id";
